import { useCallback, useRef, useState } from 'react';
import type { FilePond as FilePondInstance } from 'react-filepond';

import { finalizeBaseName, sanitizeBaseNameInput, splitFileName } from '../media-manager/sanitize-file-name';
import { centerCropToBlob } from './center-crop';

const isPdfFile = (file: File): boolean => file.type === 'application/pdf';

export function useCropQueue({
  cropAspectRatio,
  pondRef,
  onUploaded,
}: {
  cropAspectRatio: number;
  pondRef: React.RefObject<FilePondInstance | null>;
  onUploaded?: (key: string, filename: string) => void;
}) {
  const [cropOpen, setCropOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState('');
  // Editable base name (no extension) shown in the crop / rename dialogs.
  const [fileName, setFileName] = useState('');

  const fileNameRef = useRef('');
  const pendingFileIdRef = useRef<string | null>(null);
  const pendingOriginalFileRef = useRef<File | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const croppedFileRef = useRef<File | null>(null);
  const cropQueueRef = useRef<Array<{ file: File; fileId: string }>>([]);
  const cropDialogActiveRef = useRef(false);
  const reportedServerIdsRef = useRef<Set<string>>(new Set());

  const updateFileName = useCallback((value: string) => {
    fileNameRef.current = value;
    setFileName(value);
  }, []);

  /** Edited base name plus the given extension, e.g. `banner-hero.webp`. */
  const buildFileName = useCallback((extension: string): string => {
    const base = finalizeBaseName(fileNameRef.current);
    return extension ? `${base}.${extension}` : base;
  }, []);

  const openDialog = useCallback(
    (file: File, fileId: string) => {
      pendingFileIdRef.current = fileId;
      pendingOriginalFileRef.current = file;
      cropDialogActiveRef.current = true;
      updateFileName(sanitizeBaseNameInput(splitFileName(file.name).base));

      // PDFs have nothing to crop — ask for a name only.
      if (isPdfFile(file)) {
        setRenameOpen(true);
        return;
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      setCropSrc(url);
      setCropOpen(true);
    },
    [updateFileName],
  );

  const processNextInQueue = useCallback(() => {
    cropDialogActiveRef.current = false;
    const next = cropQueueRef.current.shift();
    if (next) {
      openDialog(next.file, next.fileId);
    }
  }, [openDialog]);

  /**
   * Take the pending file id exactly once. Enter key-repeat, a double click, or a click during the
   * dialog's exit transition fire the same handler again; the repeat sees `null` and must do nothing,
   * otherwise the file uploads twice and the queue advances twice.
   */
  const consumePendingFileId = useCallback((): string | null => {
    const fileId = pendingFileIdRef.current;
    pendingFileIdRef.current = null;
    return fileId;
  }, []);

  /** Swap the pending FilePond item for a renamed / cropped file, then upload it. */
  const replaceAndProcess = useCallback(
    (pond: FilePondInstance, fileId: string, replacement: File) => {
      croppedFileRef.current = replacement;
      pond.removeFile(fileId);
      pond.addFile(replacement).then(pondFile => {
        pond.processFile(pondFile.id);
        processNextInQueue();
      });
    },
    [processNextInQueue],
  );

  const handleDialogExited = useCallback(() => {
    if (cropDialogActiveRef.current) {
      return;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setCropSrc('');
  }, []);

  const handleCropConfirm = useCallback(
    (blob: Blob) => {
      const pond = pondRef.current;
      if (!pond) {
        return;
      }
      const fileId = consumePendingFileId();
      if (!fileId) {
        return;
      }

      setCropOpen(false);
      replaceAndProcess(pond, fileId, new File([blob], buildFileName('webp'), { type: 'image/webp' }));
    },
    [pondRef, buildFileName, consumePendingFileId, replaceAndProcess],
  );

  /** Upload the pending original, swapping in a renamed copy when the name changed. */
  const processOriginal = useCallback(
    (pond: FilePondInstance, fileId: string, originalFile: File | null) => {
      if (originalFile) {
        const renamed = buildFileName(splitFileName(originalFile.name).ext);
        if (renamed !== originalFile.name) {
          replaceAndProcess(pond, fileId, new File([originalFile], renamed, { type: originalFile.type }));
          return;
        }
      }
      pond.processFile(fileId);
      processNextInQueue();
    },
    [buildFileName, replaceAndProcess, processNextInQueue],
  );

  const handleCropSkip = useCallback(async () => {
    const fileId = consumePendingFileId();
    const pond = pondRef.current;
    if (!fileId) {
      return;
    }
    setCropOpen(false);

    if (!pond) {
      processNextInQueue();
      return;
    }

    const originalFile = pendingOriginalFileRef.current;
    if (!isNaN(cropAspectRatio) && originalFile) {
      try {
        const blob = await centerCropToBlob(originalFile, cropAspectRatio);
        replaceAndProcess(pond, fileId, new File([blob], buildFileName('webp'), { type: 'image/webp' }));
        return;
      } catch {
        // fall through to upload original
      }
    }

    processOriginal(pond, fileId, originalFile);
  }, [
    cropAspectRatio,
    pondRef,
    buildFileName,
    consumePendingFileId,
    replaceAndProcess,
    processOriginal,
    processNextInQueue,
  ]);

  const handleRenameConfirm = useCallback(() => {
    const fileId = consumePendingFileId();
    const pond = pondRef.current;
    if (!fileId) {
      return;
    }
    setRenameOpen(false);

    if (!pond) {
      processNextInQueue();
      return;
    }
    processOriginal(pond, fileId, pendingOriginalFileRef.current);
  }, [pondRef, consumePendingFileId, processOriginal, processNextInQueue]);

  const handleCropCancel = useCallback(() => {
    const fileId = consumePendingFileId();
    if (!fileId) {
      return;
    }
    pondRef.current?.removeFile(fileId);
    setCropOpen(false);
    setRenameOpen(false);
    processNextInQueue();
  }, [pondRef, consumePendingFileId, processNextInQueue]);

  const handleProcessFile = useCallback(
    (_error: unknown, file: { id: string; serverId: unknown; filename: string }) => {
      const key = file.serverId as string;
      if (!key) {
        return;
      }
      if (reportedServerIdsRef.current.has(key)) {
        return;
      }
      reportedServerIdsRef.current.add(key);
      onUploaded?.(key, file.filename);
      // The uploaded file is now represented in the media library. Remove the
      // temporary FilePond item so it is not shown a second time beside its
      // library thumbnail and the one-file drop zone is ready for another upload.
      pondRef.current?.removeFile(file.id);
    },
    [onUploaded, pondRef],
  );

  const onAddFile = useCallback(
    (file: File, fileId: string) => {
      if (croppedFileRef.current && file === croppedFileRef.current) {
        croppedFileRef.current = null;
        return;
      }
      if (cropDialogActiveRef.current) {
        cropQueueRef.current.push({ file, fileId });
        return;
      }
      openDialog(file, fileId);
    },
    [openDialog],
  );

  const onRemoveFile = useCallback((serverId: string | null, fileId: string) => {
    if (serverId) {
      reportedServerIdsRef.current.delete(serverId);
    }
    cropQueueRef.current = cropQueueRef.current.filter(q => q.fileId !== fileId);
  }, []);

  return {
    cropOpen,
    renameOpen,
    cropSrc,
    fileName,
    updateFileName,
    croppedFileRef,
    handleCropConfirm,
    handleCropSkip,
    handleCropCancel,
    handleRenameConfirm,
    handleDialogExited,
    handleProcessFile,
    onAddFile,
    onRemoveFile,
  };
}
